import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Smartphone, Monitor, Copy, Check, Code, Globe } from "lucide-react";

const WIDGET_URL = "https://iqtexting.com/widget";

const EMBED_SCRIPT = `<script>
(function() {
  var iframe = document.createElement('iframe');
  iframe.src = '${WIDGET_URL}';
  iframe.id = 'iqtexting-widget';
  iframe.style.cssText = 'position:fixed;bottom:24px;right:24px;width:380px;height:520px;border:none;border-radius:12px;box-shadow:0 20px 25px -5px rgba(0,0,0,0.1),0 10px 10px -5px rgba(0,0,0,0.04);z-index:9999;display:none;';
  document.body.appendChild(iframe);
  var btn = document.createElement('button');
  btn.innerHTML = '💬';
  btn.style.cssText = 'position:fixed;bottom:24px;right:24px;width:56px;height:56px;border-radius:50%;background:#3B82F6;color:white;font-size:24px;border:none;cursor:pointer;z-index:9999;box-shadow:0 4px 6px -1px rgba(0,0,0,0.1);';
  btn.onclick = function() {
    iframe.style.display = iframe.style.display === 'none' ? 'block' : 'none';
  };
  document.body.appendChild(btn);
})();
</script>`;

export const WidgetSettings = () => {
  const { currentOrganization } = useOrganization();
  const { canManageSettings } = useOrgPermissions();
  const { toast } = useToast();
  
  const [settings, setSettings] = useState({
    position_horizontal: 'right',
    position_vertical: 'bottom',
    offset_horizontal: 24,
    offset_vertical: 24,
    is_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (currentOrganization) {
      fetchSettings();
    }
  }, [currentOrganization?.id]);

  const fetchSettings = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('widget_settings' as any)
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data && typeof data === 'object') {
        setSettings(data as any);
      }
    } catch (error) {
      console.error('Error fetching widget settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentOrganization) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('widget_settings' as any)
        .upsert({
          organization_id: currentOrganization.id,
          ...settings
        });

      if (error) throw error;

      toast({
        title: "Settings saved",
        description: "Widget position settings have been updated"
      });
    } catch (error: any) {
      toast({
        title: "Error saving settings",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(EMBED_SCRIPT);
      setCopied(true);
      toast({ title: "Copied!", description: "Embed code copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Please select and copy manually", variant: "destructive" });
    }
  };

  if (!canManageSettings()) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">You don&apos;t have permission to manage widget settings.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Loading settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Widget Position Settings
          </CardTitle>
          <CardDescription>
            Configure where the chat widget appears on your website. The widget will stay in this position as users scroll.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Widget Preview */}
          <div className="border-2 border-dashed rounded-lg p-8 bg-muted/20 relative h-64">
            <div className="text-center text-sm text-muted-foreground mb-4">
              Preview - Widget Position
            </div>
            <div 
              className="absolute transition-all duration-300"
              style={{
                [settings.position_horizontal === 'left' ? 'left' : settings.position_horizontal === 'right' ? 'right' : 'left']: 
                  settings.position_horizontal === 'center' ? '50%' : `${settings.offset_horizontal}px`,
                [settings.position_vertical === 'top' ? 'top' : 'bottom']: `${settings.offset_vertical}px`,
                transform: settings.position_horizontal === 'center' ? 'translateX(-50%)' : 'none'
              }}
            >
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary to-accent flex flex-col items-center justify-center shadow-lg">
                <Smartphone className="h-8 w-8 text-white" />
                <span className="text-xs text-white font-semibold">Text</span>
              </div>
            </div>
          </div>

          {/* Enable/Disable Widget */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="widget-enabled" className="font-semibold">Enable Widget</Label>
              <p className="text-sm text-muted-foreground">Show the chat widget on your website</p>
            </div>
            <Switch
              id="widget-enabled"
              checked={settings.is_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
            />
          </div>

          {/* Horizontal Position */}
          <div className="space-y-2">
            <Label htmlFor="horizontal-position">Horizontal Position</Label>
            <Select
              value={settings.position_horizontal}
              onValueChange={(value) => setSettings({ ...settings, position_horizontal: value })}
            >
              <SelectTrigger id="horizontal-position">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Vertical Position */}
          <div className="space-y-2">
            <Label htmlFor="vertical-position">Vertical Position</Label>
            <Select
              value={settings.position_vertical}
              onValueChange={(value) => setSettings({ ...settings, position_vertical: value })}
            >
              <SelectTrigger id="vertical-position">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="top">Top</SelectItem>
                <SelectItem value="bottom">Bottom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Horizontal Offset */}
          <div className="space-y-2">
            <Label htmlFor="horizontal-offset">Horizontal Offset (pixels)</Label>
            <Input
              id="horizontal-offset"
              type="number"
              min="0"
              max="200"
              value={settings.offset_horizontal}
              onChange={(e) => setSettings({ ...settings, offset_horizontal: parseInt(e.target.value) || 0 })}
            />
            <p className="text-xs text-muted-foreground">
              Distance from the {settings.position_horizontal === 'center' ? 'center' : settings.position_horizontal} edge
            </p>
          </div>

          {/* Vertical Offset */}
          <div className="space-y-2">
            <Label htmlFor="vertical-offset">Vertical Offset (pixels)</Label>
            <Input
              id="vertical-offset"
              type="number"
              min="0"
              max="200"
              value={settings.offset_vertical}
              onChange={(e) => setSettings({ ...settings, offset_vertical: parseInt(e.target.value) || 0 })}
            />
            <p className="text-xs text-muted-foreground">
              Distance from the {settings.position_vertical} edge
            </p>
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? "Saving..." : "Save Widget Settings"}
          </Button>

          <div className="text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg">
            <strong>Note:</strong> The widget will remain fixed in this position as users scroll your website. Changes take effect immediately on your live site.
          </div>
        </CardContent>
      </Card>

      {/* Installation Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Installation Instructions
          </CardTitle>
          <CardDescription>
            Add the widget to any external website, including WordPress.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Embed Code */}
          <div className="space-y-2">
            <Label className="font-semibold flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Universal Embed Code
            </Label>
            <p className="text-sm text-muted-foreground">
              Copy and paste this code just before the closing <code>&lt;/body&gt;</code> tag on any website.
            </p>
            <div className="relative">
              <pre className="bg-muted rounded-lg p-4 text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono border">
                {EMBED_SCRIPT}
              </pre>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
                onClick={handleCopy}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* WordPress Instructions */}
          <div className="space-y-3 border-t pt-4">
            <h4 className="font-semibold flex items-center gap-2">
              <Globe className="h-4 w-4" />
              WordPress Installation
            </h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><strong>Method 1 — Theme Editor (Recommended):</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Go to <strong>Appearance → Theme File Editor</strong> in your WordPress admin.</li>
                <li>Select your active theme and open <code>footer.php</code>.</li>
                <li>Paste the embed code above just before the closing <code>&lt;/body&gt;</code> tag.</li>
                <li>Click <strong>Update File</strong> to save.</li>
              </ol>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><strong>Method 2 — Custom HTML Widget:</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Go to <strong>Appearance → Widgets</strong>.</li>
                <li>Add a <strong>Custom HTML</strong> widget to your footer area.</li>
                <li>Paste the embed code above into the widget content.</li>
                <li>Click <strong>Save</strong>.</li>
              </ol>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><strong>Method 3 — Plugin (Code Snippets):</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Install the <strong>Code Snippets</strong> plugin from the WordPress plugin directory.</li>
                <li>Go to <strong>Snippets → Add New</strong>.</li>
                <li>Set the snippet to run on the <strong>Front-end</strong> only.</li>
                <li>Paste the embed code into the snippet content.</li>
                <li>Save and activate the snippet.</li>
              </ol>
            </div>
          </div>

          <div className="text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg">
            <strong>What the widget does:</strong> Visitors on your site will see a floating chat button. When clicked, it opens a compact panel where they can send you a message (creates an SMS conversation) or book an appointment directly.
          </div>
        </CardContent>
      </Card>
    </>
  );
};
